import React, { useEffect } from 'react';
import { Card, Spin, Row, Col, Input, ColorPicker, Button } from 'antd';
import type { TenantCreate, TenantUpdate, BrandingColors } from '../types/tenant.types';
import { useTenantForm } from '../hooks/use-tenant-form.hook';

import FormBuilder, { FormField } from '../../../../common/FormBuilder';
import { useTranslation } from 'react-i18next';
import { DefaultValues } from 'react-hook-form';
import { ValidationError } from '../../../../../types/api-response.types';
import { Select, DatePicker } from 'antd';
import SelectField from '../../../../common/SelectField';
import { useCountryList } from '../../countries/hooks/use-country.hook';
import { ALLOWED_LANGUAGES } from '../../../../../const/allowed-languages.const';
import { ALLOWED_CURRENCIES } from '../../../../../const/allowed-currencies.const';
import { ALLOWED_REGIONS } from '../../../../../const/allowed-regions.const';
import { useTenantList } from '../hooks/use-tenant.hook';
import PhoneNumberInput from '../../../../common/PhoneNumberInput';
import { phoneService } from '../../../../../services/phone.service';

interface SysAdminTenantFormProps {
  defaultValues?: DefaultValues<TenantCreate | TenantUpdate>;
  onFormSubmit: (data: TenantCreate | TenantUpdate, resetForm: () => void) => void;
  isLoading: boolean;
  columns?: 1 | 2 | 3 | 4;
  validationErrors?: ValidationError[];
}

function SysAdminTenantFormComponent({
  defaultValues,
  onFormSubmit,
  isLoading = false,
  columns = 2,
  validationErrors = [],
}: SysAdminTenantFormProps) {
  const { t } = useTranslation();

  const { control, errors, resetForm, formSubmitHandler, reset, setError, setValue } =
    useTenantForm({
      defaultValues,
      onSubmit: (data) => onFormSubmit(data, resetForm),
    });

  // Reset form values when defaultValues change
  useEffect(() => {
    if (defaultValues && Object.keys(defaultValues).length > 0) {
      reset(defaultValues);
    }
  }, [defaultValues, reset]);

  // Handle API validation errors
  useEffect(() => {
    validationErrors.forEach((error) => {
      setError(error.path as any, {
        type: 'manual',
        message: error.message,
      });
    });
  }, [validationErrors, setError]);

  const formFields: FormField<TenantCreate | TenantUpdate>[] = [
    {
      name: 'name',
      label: t('tenants.name.label'),
      required: true,
      errorMsg: errors?.name?.message as string | undefined,
      rules: {
        required: 'Name is required',
        minLength: { value: 3, message: 'Name must be at least 3 characters' },
        maxLength: { value: 50, message: 'Name must be at most 50 characters' },
      },
      render: (field) => (
        <Input {...field} disabled={isLoading} placeholder={t('tenants.name.placeholder')} />
      ),
    },
    {
      name: 'addressLine1',
      label: t('tenants.addressLine1.label'),
      required: true,
      errorMsg: errors?.addressLine1?.message as string | undefined,
      rules: {
        required: 'Address Line 1 is required',
        minLength: { value: 3, message: 'Address Line 1 must be at least 3 characters' },
        maxLength: { value: 50, message: 'Address Line 1 must be at most 50 characters' },
      },
      render: (field) => (
        <Input
          {...field}
          disabled={isLoading}
          placeholder={t('tenants.addressLine1.placeholder')}
        />
      ),
    },
    {
      name: 'addressLine2',
      label: t('tenants.addressLine2.label'),
      required: false,
      errorMsg: errors?.addressLine2?.message as string | undefined,
      rules: {
        minLength: { value: 3, message: 'Address Line 2 must be at least 3 characters' },
        maxLength: { value: 50, message: 'Address Line 2 must be at most 50 characters' },
      },
      render: (field) => (
        <Input
          {...field}
          disabled={isLoading}
          placeholder={t('tenants.addressLine2.placeholder')}
        />
      ),
    },
    {
      name: 'city',
      label: t('tenants.city.label'),
      required: true,
      errorMsg: errors?.city?.message as string | undefined,
      rules: {
        required: 'City is required',
        minLength: { value: 3, message: 'City must be at least 3 characters' },
        maxLength: { value: 20, message: 'City must be at most 20 characters' },
      },
      render: (field) => (
        <Input {...field} disabled={isLoading} placeholder={t('tenants.city.placeholder')} />
      ),
    },
    {
      name: 'state',
      label: t('tenants.state.label'),
      required: false,
      errorMsg: errors?.state?.message as string | undefined,
      rules: {
        minLength: { value: 3, message: 'State must be at least 3 characters' },
        maxLength: { value: 20, message: 'State must be at most 20 characters' },
      },
      render: (field) => (
        <Input {...field} disabled={isLoading} placeholder={t('tenants.state.placeholder')} />
      ),
    },
    {
      name: 'zipCode',
      label: t('tenants.zipCode.label'),
      required: false,
      errorMsg: errors?.zipCode?.message as string | undefined,
      rules: {
        minLength: { value: 3, message: 'Zip Code must be at least 3 characters' },
        maxLength: { value: 10, message: 'Zip Code must be at most 10 characters' },
      },
      render: (field) => (
        <Input {...field} disabled={isLoading} placeholder={t('tenants.zipCode.placeholder')} />
      ),
    },
    {
      name: 'countryId',
      label: t('tenants.countryId.label'),
      required: true,
      errorMsg: errors?.countryId?.message as string | undefined,
      rules: { required: 'Country Id is required' },
      render: (field) => (
        <SelectField
          {...field}
          disabled={isLoading}
          placeholder={t('tenants.countryId.placeholder')}
          showSearch
          optionFilterProp="children"
          useListHook={useCountryList}
          labelKey="code"
          valueKey="id"
          value={field.value}
          onChange={(value) => {
            setValue('countryId', value as string);
          }}
        />
      ),
    },
    {
      name: 'region',
      label: t('tenants.region.label'),
      required: true,
      errorMsg: errors?.region?.message as string | undefined,
      rules: {
        required: 'Region is required',
      },
      render: (field) => (
        <Select
          {...field}
          disabled={isLoading}
          placeholder={t('tenants.region.placeholder')}
          showSearch
          optionFilterProp="children"
        >
          {ALLOWED_REGIONS.map((region) => (
            <Select.Option key={region} value={region}>
              {region.toUpperCase()}
            </Select.Option>
          ))}
        </Select>
      ),
    },
    {
      name: 'primaryEmail',
      label: t('tenants.primaryEmail.label'),
      required: true,
      errorMsg: errors?.primaryEmail?.message as string | undefined,
      rules: {
        required: 'Primary Email is required',
        minLength: { value: 3, message: 'Primary Email must be at least 3 characters' },
        maxLength: { value: 50, message: 'Primary Email must be at most 50 characters' },
      },
      render: (field) => (
        <Input
          type="email"
          {...field}
          disabled={isLoading}
          placeholder={t('tenants.primaryEmail.placeholder')}
        />
      ),
    },
    {
      name: 'secondaryEmail',
      label: t('tenants.secondaryEmail.label'),
      required: false,
      errorMsg: errors?.secondaryEmail?.message as string | undefined,
      rules: {
        minLength: { value: 3, message: 'Secondary Email must be at least 3 characters' },
        maxLength: { value: 50, message: 'Secondary Email must be at most 50 characters' },
      },
      render: (field) => (
        <Input
          type="email"
          {...field}
          disabled={isLoading}
          placeholder={t('tenants.secondaryEmail.placeholder')}
        />
      ),
    },
    {
      name: 'primaryPhone',
      label: t('tenants.primaryPhone.label'),
      required: true,
      errorMsg: errors?.primaryPhone?.message as string | undefined,
      rules: {
        required: 'Primary Phone is required',
        minLength: { value: 3, message: 'Primary Phone must be at least 3 characters' },
        maxLength: { value: 15, message: 'Primary Phone must be at most 15 characters' },
        validate: (value: string) =>
          !value || phoneService.isValid(value) || 'Invalid phone number',
      },
      render: (field) => (
        <PhoneNumberInput
          {...field}
          disabled={isLoading}
          placeholder={t('tenants.primaryPhone.placeholder')}
          defaultCountryCode="AE"
        />
      ),
    },
    {
      name: 'secondaryPhone',
      label: t('tenants.secondaryPhone.label'),
      required: false,
      errorMsg: errors?.secondaryPhone?.message as string | undefined,
      rules: {
        minLength: { value: 3, message: 'Secondary Phone must be at least 3 characters' },
        maxLength: { value: 15, message: 'Secondary Phone must be at most 15 characters' },
        validate: (value: string) =>
          !value || phoneService.isValid(value) || 'Invalid phone number',
      },
      render: (field) => (
        <PhoneNumberInput
          {...field}
          disabled={isLoading}
          placeholder={t('tenants.secondaryPhone.placeholder')}
          defaultCountryCode="AE"
        />
      ),
    },
    {
      name: 'defaultLanguage',
      label: t('tenants.defaultLanguage.label'),
      required: true,
      errorMsg: errors?.defaultLanguage?.message as string | undefined,
      rules: {
        required: 'Default Language is required',
        minLength: { value: 2, message: 'Default Language must be at least 2 characters' },
        maxLength: { value: 2, message: 'Default Language must be at most 2 characters' },
      },
      render: (field) => (
        <Select
          {...field}
          disabled={isLoading}
          placeholder={t('tenants.defaultLanguage.placeholder')}
          showSearch
          optionFilterProp="children"
        >
          {ALLOWED_LANGUAGES.map((language) => (
            <Select.Option key={language} value={language}>
              {language.toUpperCase()}
            </Select.Option>
          ))}
        </Select>
      ),
    },
    {
      name: 'defaultCurrency',
      label: t('tenants.defaultCurrency.label'),
      required: true,
      errorMsg: errors?.defaultCurrency?.message as string | undefined,
      rules: {
        required: 'Default Currency is required',
        minLength: { value: 2, message: 'Default Currency must be at least 2 characters' },
        maxLength: { value: 5, message: 'Default Currency must be at most 5 characters' },
      },
      render: (field) => (
        <Select
          {...field}
          disabled={isLoading}
          placeholder={t('tenants.defaultCurrency.placeholder')}
          showSearch
          optionFilterProp="children"
        >
          {ALLOWED_CURRENCIES.map((currency) => (
            <Select.Option key={currency} value={currency}>
              {currency}
            </Select.Option>
          ))}
        </Select>
      ),
    },
    {
      name: 'parentTenantId',
      label: t('tenants.parentTenantId.label'),
      required: false,
      errorMsg: errors?.parentTenantId?.message as string | undefined,
      rules: {},
      render: (field) => (
        <SelectField
          {...field}
          disabled={isLoading}
          placeholder={t('tenants.parentTenantId.placeholder')}
          useListHook={useTenantList}
          labelKey="name"
          valueKey="id"
          value={field.value}
          onChange={(value) => {
            setValue('parentTenantId', value as string);
          }}
        />
      ),
    },
    {
      name: 'brandingColors',
      label: t('tenants.brandingColors.label'),
      required: false,
      errorMsg: errors?.brandingColors?.message as string | undefined,
      rules: {},
      render: (field) => {
        const currentValue = (field.value as BrandingColors) || {
          primary: '#1890ff',
          secondary: '#52c41a',
        };

        const handleColorChange = (colorType: 'primary' | 'secondary', color: string) => {
          const newValue = {
            ...currentValue,
            [colorType]: color,
          };
          setValue('brandingColors', newValue);
        };

        return (
          <div>
            <Row gutter={16}>
              <Col span={12}>
                <div>
                  <ColorPicker
                    value={currentValue.primary}
                    onChange={(color) =>
                      handleColorChange('primary', color?.toHexString() || '#1890ff')
                    }
                    disabled={isLoading}
                  >
                    <Button
                      type="primary"
                      style={{
                        backgroundColor: currentValue.primary,
                        borderColor: currentValue.primary,
                        width: '100%',
                      }}
                    >
                      {t('tenants.brandingColors.primary')}
                    </Button>
                  </ColorPicker>
                </div>
              </Col>
              <Col span={12}>
                <div>
                  <ColorPicker
                    value={currentValue.secondary}
                    onChange={(color) =>
                      handleColorChange('secondary', color?.toHexString() || '#52c41a')
                    }
                    disabled={isLoading}
                  >
                    <Button
                      type="primary"
                      style={{
                        backgroundColor: currentValue.secondary,
                        borderColor: currentValue.secondary,
                        width: '100%',
                      }}
                    >
                      {t('tenants.brandingColors.secondary')}
                    </Button>
                  </ColorPicker>
                </div>
              </Col>
            </Row>
          </div>
        );
      },
    },
    {
      name: 'billingPlanId',
      label: t('tenants.billingPlanId.label'),
      required: false,
      errorMsg: errors?.billingPlanId?.message as string | undefined,
      rules: {},
      render: (field) => (
        <Input
          {...field}
          disabled={isLoading}
          placeholder={t('tenants.billingPlanId.placeholder')}
        />
      ),
    },
    {
      name: 'supportPlanId',
      label: t('tenants.supportPlanId.label'),
      required: false,
      errorMsg: errors?.supportPlanId?.message as string | undefined,
      rules: {},
      render: (field) => (
        <Input
          {...field}
          disabled={isLoading}
          placeholder={t('tenants.supportPlanId.placeholder')}
        />
      ),
    },
    {
      name: 'contractStartDate',
      label: t('tenants.contractStartDate.label'),
      required: true,
      errorMsg: errors?.contractStartDate?.message as string | undefined,
      rules: { required: 'Contract Start Date is required' },
      render: (field) => (
        <DatePicker
          {...field}
          disabled={isLoading}
          format="YYYY-MM-DD"
          style={{ width: '100%' }}
          placeholder={t('tenants.contractStartDate.placeholder')}
        />
      ),
    },
    {
      name: 'contractEndDate',
      label: t('tenants.contractEndDate.label'),
      required: false,
      errorMsg: errors?.contractEndDate?.message as string | undefined,
      rules: {},
      render: (field) => (
        <DatePicker
          {...field}
          disabled={isLoading}
          format="YYYY-MM-DD"
          style={{ width: '100%' }}
          placeholder={t('tenants.contractEndDate.placeholder')}
        />
      ),
    },
    {
      name: 'notes',
      label: t('tenants.notes.label'),
      required: false,
      errorMsg: errors?.notes?.message as string | undefined,
      rules: {},
      render: (field) => (
        <Input {...field} disabled={isLoading} placeholder={t('tenants.notes.placeholder')} />
      ),
    },
  ];

  return (
    <Spin spinning={isLoading} tip={t('common.submittingForm')}>
      <Card className="shadow-lg">
        <FormBuilder
          formFields={formFields}
          control={control}
          errors={errors}
          onSubmit={formSubmitHandler()}
          submitButtonText={t('common.save')}
          isLoading={isLoading}
          columns={columns}
        />
      </Card>
    </Spin>
  );
}

// Custom comparison function for memoization
function areEqual(prevProps: SysAdminTenantFormProps, nextProps: SysAdminTenantFormProps): boolean {
  return (
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.onFormSubmit === nextProps.onFormSubmit &&
    prevProps.columns === nextProps.columns &&
    JSON.stringify(prevProps.defaultValues) === JSON.stringify(nextProps.defaultValues) &&
    JSON.stringify(prevProps.validationErrors) === JSON.stringify(nextProps.validationErrors)
  );
}

export default React.memo(SysAdminTenantFormComponent, areEqual);
